import { useState, useEffect, useRef } from "react";
import { AIFlowchartContent } from "../../types/ai-templates";

export function FlowchartTemplate5({
  content,
  onUpdate,
}: {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [aspectRatio] = useState<number>(864 / 520); // Updated aspect ratio

  // Maintain aspect ratio on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = width / aspectRatio;
        containerRef.current.style.height = `${height}px`;
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => window.removeEventListener("resize", updateDimensions);
  }, [aspectRatio]);

  // Define colors for light mode
  const colors = {
    background: "#FFFFFF",
    blue: "#1ac3fb",
    green: "#43dd93",
    yellow: "#ffe711",
    orange: "#f79438",
    gray: "#b7b7b7",
    textHeading: "#1F2937",
    textDescription: "#4B5563",
  };

  // Define colors for each section
  const quadrantColors = {
    topLeft: {
      fill: "#E6F0FA",
      stroke: colors.blue,
      animatedStroke: "#00ade7d7",
    },
    topRight: {
      fill: "#E6F7EC",
      stroke: colors.green,
      animatedStroke: "#00c5676e",
    },
    middle: {
      fill: "#FFF9E6",
      stroke: colors.yellow,
      animatedStroke: "#e9d100ff",
    },
    bottomLeft: {
      fill: "#FFF4E6",
      stroke: colors.orange,
      animatedStroke: "#f57600ab",
    },
    bottomRight: {
      fill: "#F3F4F6",
      stroke: colors.gray,
      animatedStroke: "#0000001b",
    },
  };

  // Get color for step based on index
  const getStepColor = (index: number) => {
    switch (index % 5) {
      case 0:
        return colors.blue;
      case 1:
        return colors.green;
      case 2:
        return colors.yellow;
      case 3:
        return colors.orange;
      case 4:
        return colors.gray;
      default:
        return colors.blue;
    }
  };

  // Get the actual title text based on the section position
  const getQuadrantTitle = (index: number, step: any): string => {
    const defaultTitles = {
      0: "Valuable Information",
      1: "Interest and Engagement",
      2: "Team Dynamics",
      3: "Growth Opportunities",
      4: "Company Culture",
    };
    return step.title || defaultTitles[index % 5] || `Step ${index + 1}`;
  };

  // Format title display for multi-line titles
  const formatTitleDisplay = (index: number, title: string) => {
    const parts = title.split(" ");
    if (parts.length > 1) {
      const mid = Math.ceil(parts.length / 2);
      return (
        <>
          <div className="mb-1 whitespace-nowrap">
            {parts.slice(0, mid).join(" ")}
          </div>
          <div>{parts.slice(mid).join(" ")}</div>
        </>
      );
    }
    return title;
  };

  // Handle content update
  const handleContentUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Render SVG icons for each step
  const renderStepIcon = (index: number, color: string) => {
    const icons = [
      // Icon for Step 1: Valuable Information
      <svg
        key="icon-1"
        width="min(5vw,1.5rem)"
        height="min(5vw,1.5rem)"
        viewBox="0 0 68 68"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M35.812 56.488H13.068C11.9259 56.488 11 55.5621 11 54.42V13.068C11 11.9259 11.9259 11 13.068 11H47.184C48.3261 11 49.252 11.9259 49.252 13.068V29.6M16.584 38.122H27.54M16.584 46.402H27.54M36.468 27.794H43.954M34.256 43.622C34.256 48.7947 38.4493 52.988 43.622 52.988C48.7947 52.988 52.988 48.7947 52.988 43.622C52.988 38.4493 48.7947 34.256 43.622 34.256C38.4493 34.256 34.256 38.4493 34.256 43.622M57 57L50.286 50.286M19.604 19.78C19.6037 21.7276 21.1824 23.3066 23.13 23.3066C25.0776 23.3066 26.6563 21.7276 26.656 19.78C26.6563 17.8324 25.0776 16.2534 23.13 16.2534C21.1824 16.2534 19.6037 17.8324 19.604 19.78M28.892 30.358C28.2209 27.7236 25.8486 25.8797 23.13 25.8797C20.4114 25.8797 18.0391 27.7236 17.368 30.358M36.468 19.878H40.674"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
      // Icon for Step 2: Interest and Engagement
      <svg
        key="icon-2"
        width="min(5vw,1.5rem)"
        height="min(5vw,1.5rem)"
        viewBox="0 0 68 68"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M33.4 28.56C33.7577 28.8202 34.2423 28.8202 34.6 28.56C36.76 26.9 44 21 44 16.56C44 10.42 35.82 8.56 34 15.94C32.18 8.36 24 10.32 24 16.46C24 21 31.24 26.9 33.4 28.56ZM15 35C15 38.3137 17.6863 41 21 41C24.3137 41 27 38.3137 27 35C27 31.6863 24.3137 29 21 29C17.6863 29 15 31.6863 15 35M15.82 32C17.7101 33.9284 20.2998 35.0104 23 35C24.3755 35.0047 25.7373 34.7255 27 34.18M13 57V52C13 47.5817 16.5817 44 21 44C25.4183 44 29 47.5817 29 52V57M41 35C41 38.3137 43.6863 41 47 41C50.3137 41 53 38.3137 53 35C53 31.6863 50.3137 29 47 29C43.6863 29 41 31.6863 41 35M41.08 36C43.381 35.992 45.5671 34.9937 47.08 33.26C48.5929 34.9937 50.779 35.992 53.08 36M55 57L53.12 48.88C52.4638 46.0289 49.9257 44.0085 47 44.0085C44.0743 44.0085 41.5362 46.0289 40.88 48.88L39 57M43.2 45.28L47 51L50.8 45.28"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
      // Icon for Step 3: Team Dynamics
      <svg
        key="icon-3"
        width="min(5vw,1.5rem)"
        height="min(5vw,1.5rem)"
        viewBox="0 0 68 68"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M44.25 46C44.25 47.2426 45.2574 48.25 46.5 48.25C47.7426 48.25 48.75 47.2426 48.75 46C48.75 44.7574 47.7426 43.75 46.5 43.75C45.2574 43.75 44.25 44.7574 44.25 46M48.412 37.296L49.074 39.474C49.3004 40.2247 50.0698 40.6715 50.834 40.496L53.034 39.984C53.8967 39.7777 54.7922 40.1643 55.2337 40.9336C55.6752 41.703 55.5573 42.6712 54.944 43.312L53.4 44.978C52.8659 45.5547 52.8659 46.4453 53.4 47.022L54.944 48.688C55.5573 49.3288 55.6752 50.297 55.2337 51.0664C54.7922 51.8357 53.8967 52.2223 53.034 52.016L50.834 51.504C50.0698 51.3285 49.3004 51.7753 49.074 52.526L48.412 54.704C48.1543 55.5439 47.3786 56.1173 46.5 56.1173C45.6214 56.1173 44.8457 55.5439 44.588 54.704L43.926 52.526C43.6996 51.7753 42.9302 51.3285 42.166 51.504L39.966 52.016C39.1033 52.2223 38.2078 51.8357 37.7663 51.0664C37.3248 50.297 37.4427 49.3288 38.056 48.688L39.6 47.022C40.1341 46.4453 40.1341 45.5547 39.6 44.978L38.056 43.312C37.4427 42.6712 37.3248 41.703 37.7663 40.9336C38.2078 40.1643 39.1033 39.7777 39.966 39.984L42.166 40.496C42.9302 40.6715 43.6996 40.2247 43.926 39.474L44.588 37.296C44.8457 36.4561 45.6214 35.8827 46.5 35.8827C47.3786 35.8827 48.1543 36.4561 48.412 37.296ZM33.5 55.876H11.5V39.876C11.5 38.7714 12.3954 37.876 13.5 37.876H31.5L45.5 23.876L56.5 34.876M15.5 37.876L17.5 17.876H25L27 37.876M50.5 11.876C48.7124 11.881 47.0649 12.8445 46.184 14.4C44.9337 13.5522 43.248 13.7663 42.2493 14.8998C41.2507 16.0334 41.2507 17.7326 42.2493 18.8662C43.248 19.9997 44.9337 20.2138 46.184 19.366C47.3944 21.4736 49.945 22.4056 52.229 21.5748C54.513 20.744 55.8688 18.3911 55.4423 15.9985C55.0157 13.6058 52.9304 11.8663 50.5 11.876ZM31.5 16.876C31.5 18.5329 32.8431 19.876 34.5 19.876C36.1569 19.876 37.5 18.5329 37.5 16.876C37.5 15.2191 36.1569 13.876 34.5 13.876C32.8431 13.876 31.5 15.2191 31.5 16.876M17.5 25.876H25.5"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
      // Icon for Step 4: Growth Opportunities
      <svg
        key="icon-4"
        width="min(5vw,1.5rem)"
        height="min(5vw,1.5rem)"
        viewBox="0 0 68 68"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M34 11.532C29 11.532 25 15.532 25 20.532C25 25.532 29 29.532 34 29.532C39 29.532 43 25.532 43 20.532C43 15.532 39 11.532 34 11.532ZM10 10M31.5 24.332L29.9 19.732L33.9 16.732L37.9 19.732L36.5 24.332H31.5ZM33.9 11.532V16.732M37.9 19.732L42.5 17.532M29.9 19.732L25.5 17.532M36.5 24.332L39.5 27.732M31.5 24.332L28.5 27.732M16.7 48.9C19.5719 48.9 21.9 46.5719 21.9 43.7C21.9 40.8281 19.5719 38.5 16.7 38.5C13.8281 38.5 11.5 40.8281 11.5 43.7C11.5 46.5719 13.8281 48.9 16.7 48.9ZM25.1 55.9C23.7 52.7 20.5 50.5 16.9 50.5C14.9 50.5 13.1 51.1 11.7 52.1M51.3 48.9C54.1719 48.9 56.5 46.5719 56.5 43.7C56.5 40.8281 54.1719 38.5 51.3 38.5C48.4281 38.5 46.1 40.8281 46.1 43.7C46.1 46.5719 48.4281 48.9 51.3 48.9ZM42.9 55.9C44.3 52.7 47.5 50.5 51.1 50.5C53.1 50.5 54.9 51.1 56.3 52.1M33.9 49.1C37.6555 49.1 40.7 46.0555 40.7 42.3C40.7 38.5445 37.6555 35.5 33.9 35.5C30.1445 35.5 27.1 38.5445 27.1 42.3C27.1 46.0555 30.1445 49.1 33.9 49.1ZM43.5 56.5C41.3 53.7 37.9 52.1 34.1 52.1C30.3 52.1 26.9 53.9 24.7 56.5"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
      // Icon for Step 5: Company Culture
      <svg
        key="icon-5"
        width="min(5vw,1.5rem)"
        height="min(5vw,1.5rem)"
        viewBox="0 0 68 68"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M57 21V11H47M57 11L47.152 22.638C46.4955 23.4137 45.3616 23.5737 44.516 23.01L41.526 21.01C40.6628 20.4338 39.5024 20.6136 38.854 21.424L32.976 28.78M25 57L26 45H31V38C31 32.4772 26.5228 28 21 28C15.4772 28 11 32.4772 11 38V45H16L17 57M14 18C14 21.866 17.134 25 21 25C24.866 25 28 21.866 28 18C28 14.134 24.866 11 21 11C17.134 11 14 14.134 14 18"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>,
    ];

    return icons[index % icons.length];
  };

  // Render main title
  const renderTitle = () => {
    const title = content.title || "Enhancing Candidate Engagement";

    return (
      <div
        className="absolute left-1/2 transform -translate-x-1/2 z-40"
        style={{ top: "5%" }}
      >
        <h2
          className="text-[min(4vw,1.5rem)] md:text-[min(3vw,2rem)] font-semibold text-center"
          style={{ color: colors.textHeading }}
        >
          {title}
        </h2>
      </div>
    );
  };

  // Render section text with icons
  const renderQuadrantText = (index: number) => {
    if (index >= content.steps.length) return null;

    const step = content.steps[index];
    const stepColor = getStepColor(index);
    const isEditingTitle = editingIndex === index && editingField === "title";
    const isEditingDescription =
      editingIndex === index && editingField === "description";

    // Adjusted text positions for compact layout
    let titlePos, descPos, iconPos;
    switch (index % 5) {
      case 0: // Top-left
        titlePos = { left: "10%", top: "25%" };
        descPos = { left: "10%", top: "30%" };
        iconPos = { left: "7%", top: "25%" };
        break;
      case 1: // Top-right
        titlePos = { left: "75%", top: "25%" };
        descPos = { left: "75%", top: "30%" };
        iconPos = { left: "72%", top: "25%" };
        break;
      case 2: // Middle
        titlePos = { left: "75%", top: "55%" };
        descPos = { left: "75%", top: "60%" };
        iconPos = { left: "72%", top: "55%" };
        break;
      case 3: // Bottom-left
        titlePos = { left: "10%", top: "70%" };
        descPos = { left: "10%", top: "75%" };
        iconPos = { left: "7%", top: "70%" };
        break;
      case 4: // Bottom-right
        titlePos = { left: "10%", top: "50%" };
        descPos = { left: "10%", top: "55%" };
        iconPos = { left: "7%", top: "50%" };
        break;
      default:
        titlePos = { left: "0%", top: "0%" };
        descPos = { left: "0%", top: "0%" };
        iconPos = { left: "0%", top: "0%" };
    }

    const titleText = getQuadrantTitle(index, step);

    return (
      <>
        {/* Icon */}
        <div
          className="absolute"
          style={{
            left: iconPos.left,
            top: iconPos.top,
            zIndex: 10,
          }}
        >
          {renderStepIcon(index, stepColor)}
        </div>

        {/* Title */}
        <div
          className="absolute"
          style={{
            left: titlePos.left,
            top: titlePos.top,
            zIndex: 10,
            maxWidth: "min(40vw,180px)",
          }}
        >
          {isEditingTitle ? (
            <input
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== titleText) {
                  handleContentUpdate(index, "title", tempValue);
                }
              }}
              className="text-[min(3.5vw,1rem)] md:text-[min(2.5vw,1.125rem)] font-semibold bg-white border border-gray-300 px-2 py-1 w-full rounded"
              style={{ color: stepColor }}
            />
          ) : (
            <h3
              className="text-[min(3.5vw,1rem)] md:text-[min(2.5vw,1.125rem)] font-semibold cursor-pointer flex gap-2"
              style={{ color: stepColor }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("title");
                setTempValue(titleText);
              }}
            >
              {formatTitleDisplay(index, titleText)}
            </h3>
          )}
        </div>

        {/* Description */}
        <div
          className="absolute"
          style={{
            left: descPos.left,
            top: descPos.top,
            zIndex: 10,
            maxWidth: "min(40vw,250px)",
          }}
        >
          {isEditingDescription ? (
            <textarea
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== step.description) {
                  handleContentUpdate(index, "description", tempValue);
                }
              }}
              className="text-[min(3vw,0.875rem)] md:text-[min(2vw,1rem)] bg-white border border-gray-300 px-2 py-1 w-full rounded"
              style={{ color: colors.textDescription }}
              onFocus={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
            />
          ) : (
            <p
              className="text-[min(3vw,0.875rem)] md:text-[min(2vw,1rem)] cursor-pointer leading-relaxed"
              style={{ color: colors.textDescription }}
              onClick={() => {
                setEditingIndex(index);
                setEditingField("description");
                setTempValue(step.description || "");
              }}
            >
              {step.description}
            </p>
          )}
        </div>
      </>
    );
  };

  // Render SVG shapes
  const renderQuadrantShapes = () => {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 864 520" // Updated viewBox to reduce height
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
        `}</style>
        <g transform="translate(18, 12)">
          <g stroke="none" fill={colors.background}>
            <path d="M 0 0L 828 0L 828 496L 0 496Z" />{" "}
            {/* Adjusted background path */}
          </g>
        </g>
        {/* Top-left (Valuable Information) */}
        <g transform="translate(335, 124)">
          {" "}
          {/* Adjusted y-position */}
          <g stroke="none" fill={quadrantColors.topLeft.fill}>
            <path d="M 184.07 50.42C 157.06 55.32 127.81 58 97.25 58C 66.69 58 37.12 55.29 10 50.34C 30.9 25.66 62.13 10 97 10C 114.6 10 131.26 13.99 146.14 21.11C 160.79 28.11 173.71 38.16 184.07 50.42Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.topLeft.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 184.07 50.42C 157.06 55.32 127.81 58 97.25 58C 66.69 58 37.12 55.29 10 50.34C 30.9 25.66 62.13 10 97 10C 114.6 10 131.26 13.99 146.14 21.11C 160.79 28.11 173.71 38.16 184.07 50.42Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.topLeft.animatedStroke}
            strokeWidth="2"
            strokeDasharray="4,6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 184.07 50.42C 157.06 55.32 127.81 58 97.25 58C 66.69 58 37.12 55.29 10 50.34C 30.9 25.66 62.13 10 97 10C 114.6 10 131.26 13.99 146.14 21.11C 160.79 28.11 173.71 38.16 184.07 50.42Z" />
          </g>
        </g>
        {/* Top-right (Interest and Engagement) */}
        <g transform="translate(309.61, 164.34)">
          {" "}
          {/* Adjusted y-position */}
          <g stroke="none" fill={quadrantColors.topRight.fill}>
            <path d="M 234.82 64.74C 201.22 73 163.18 77.66 122.89 77.66C 82.6 77.66 43.84 72.91 10 64.5C 10.68 60.46 11.57 56.5 12.67 52.62C 14.88 44.78 17.91 37.29 21.65 30.24C 25.4 23.19 29.87 16.58 34.96 10.5C 35.1 10.33 35.24 10.17 35.39 10C 62.51 14.95 91.92 17.66 122.64 17.66C 153.36 17.66 182.45 14.98 209.46 10.08C 209.64 10.27 209.8 10.47 209.96 10.66C 220.14 22.86 227.82 37.22 232.21 52.95C 233.28 56.8 234.16 60.73 234.82 64.74Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.topRight.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 234.82 64.74C 201.22 73 163.18 77.66 122.89 77.66C 82.6 77.66 43.84 72.91 10 64.5C 10.68 60.46 11.57 56.5 12.67 52.62C 14.88 44.78 17.91 37.29 21.65 30.24C 25.4 23.19 29.87 16.58 34.96 10.5C 35.1 10.33 35.24 10.17 35.39 10C 62.51 14.95 91.92 17.66 122.64 17.66C 153.36 17.66 182.45 14.98 209.46 10.08C 209.64 10.27 209.8 10.47 209.96 10.66C 220.14 22.86 227.82 37.22 232.21 52.95C 233.28 56.8 234.16 60.73 234.82 64.74Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.topRight.animatedStroke}
            strokeWidth="2"
            strokeDasharray="4,6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 234.82 64.74C 201.22 73 163.18 77.66 122.89 77.66C 82.6 77.66 43.84 72.91 10 64.5C 10.68 60.46 11.57 56.5 12.67 52.62C 14.88 44.78 17.91 37.29 21.65 30.24C 25.4 23.19 29.87 16.58 34.96 10.5C 35.1 10.33 35.24 10.17 35.39 10C 62.51 14.95 91.92 17.66 122.64 17.66C 153.36 17.66 182.45 14.98 209.46 10.08C 209.64 10.27 209.8 10.47 209.96 10.66C 220.14 22.86 227.82 37.22 232.21 52.95C 233.28 56.8 234.16 60.73 234.82 64.74Z" />
          </g>
        </g>
        {/* Middle (Team Dynamics) */}
        <g transform="translate(316.13, 280.34)">
          {" "}
          {/* Adjusted y-position */}
          <g stroke="none" fill={quadrantColors.middle.fill}>
            <path d="M 221.59 10.38C 220.99 11.87 220.35 13.35 219.69 14.81C 215.43 24.18 209.95 32.86 203.43 40.67C 198.71 46.33 193.44 51.52 187.72 56.17C 180.36 62.16 174.38 69.92 171.58 78.72C 153.98 80.65 135.67 81.66 116.87 81.66C 98.07 81.66 78.31 80.56 60.08 78.49C 57.26 69.79 51.32 62.1 44.02 56.17C 38.22 51.46 32.89 46.18 28.12 40.44C 21.61 32.6 16.13 23.87 11.89 14.47C 11.23 13 10.59 11.51 10 10C 42.27 17.48 78.43 21.66 116.62 21.66C 154.81 21.66 189.74 17.62 221.59 10.38Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.middle.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 221.59 10.38C 220.99 11.87 220.35 13.35 219.69 14.81C 215.43 24.18 209.95 32.86 203.43 40.67C 198.71 46.33 193.44 51.52 187.72 56.17C 180.36 62.16 174.38 69.92 171.58 78.72C 153.98 80.65 135.67 81.66 116.87 81.66C 98.07 81.66 78.31 80.56 60.08 78.49C 57.26 69.79 51.32 62.1 44.02 56.17C 38.22 51.46 32.89 46.18 28.12 40.44C 21.61 32.6 16.13 23.87 11.89 14.47C 11.23 13 10.59 11.51 10 10C 42.27 17.48 78.43 21.66 116.62 21.66C 154.81 21.66 189.74 17.62 221.59 10.38Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.middle.animatedStroke}
            strokeWidth="2"
            strokeDasharray="4,6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 221.59 10.38C 220.99 11.87 220.35 13.35 219.69 14.81C 215.43 24.18 209.95 32.86 203.43 40.67C 198.71 46.33 193.44 51.52 187.72 56.17C 180.36 62.16 174.38 69.92 171.58 78.72C 153.98 80.65 135.67 81.66 116.87 81.66C 98.07 81.66 78.31 80.56 60.08 78.49C 57.26 69.79 51.32 62.1 44.02 56.17C 38.22 51.46 32.89 46.18 28.12 40.44C 21.61 32.6 16.13 23.87 11.89 14.47C 11.23 13 10.59 11.51 10 10C 42.27 17.48 78.43 21.66 116.62 21.66C 154.81 21.66 189.74 17.62 221.59 10.38Z" />
          </g>
        </g>
        {/* Bottom-left (Growth Opportunities) */}
        <g transform="translate(365.95, 348.83)">
          {" "}
          {/* Adjusted y-position */}
          <g stroke="none" fill={quadrantColors.bottomLeft.fill}>
            <path d="M 121.014996 10.23C 119.919996 13.71 119.312996 17.34 119.312996 21.09L 119.312996 59.87C 119.312996 66.06 114.622996 71.25 108.469996 71.69C 95.227796 72.65 80.940196 73.17 66.045296 73.17C 51.150296 73.17 36.125896 72.62 22.594996 71.62C 16.461796 71.16 11.782196 65.98 11.782196 59.79L 11.782196 21.09C 11.782196 17.26 11.144996 13.55 9.999996 10C 28.150696 12.07 47.087996 13.17 66.543096 13.17C 85.998096 13.17 103.491696 12.16 121.014996 10.23Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.bottomLeft.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 121.015196 10.23C 119.919996 13.71 119.312696 17.34 119.312696 21.09L 119.312696 59.87C 119.312696 66.06 114.623096 71.25 108.469996 71.69C 95.227796 72.65 80.940196 73.17 66.045296 73.17C 51.150296 73.17 36.125896 72.62 22.594996 71.62C 16.461796 71.16 11.782196 65.98 11.782196 59.79L 11.782196 21.09C 11.782196 17.26 11.144996 13.55 9.999996 10C 28.150696 12.07 47.087996 13.17 66.543096 13.17C 85.998096 13.17 103.491696 12.16 121.015196 10.23Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.bottomLeft.animatedStroke}
            strokeWidth="2"
            strokeDasharray="4,6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 121.015196 10.23C 119.919996 13.71 119.312696 17.34 119.312696 21.09L 119.312696 59.87C 119.312696 66.06 114.622996 71.25 108.469996 71.69C 95.227796 72.65 80.940196 73.17 66.045296 73.17C 51.150296 73.17 36.125896 72.62 22.594996 71.62C 16.461796 71.16 11.782196 65.98 11.782196 59.79L 11.782196 21.09C 11.782196 17.26 11.144996 13.55 9.999996 10C 28.150696 12.07 47.087996 13.17 66.543096 13.17C 85.998096 13.17 103.491696 12.16 121.015196 10.23Z" />
          </g>
        </g>
        {/* Bottom-right (Company Culture) */}
        <g transform="translate(308, 218.84)">
          {" "}
          {/* Adjusted y-position */}
          <g stroke="none" fill={quadrantColors.bottomRight.fill}>
            <path d="M 238 29.16C 238 35.01 237.56 40.76 236.71 46.37C 235.37 55.25 232.99 63.8 229.72 71.88C 197.87 79.12 162.29 83.16 124.75 83.16C 87.21 83.16 50.4 78.98 18.13 71.5C 14.88 63.4 12.54 54.84 11.23 45.94C 10.42 40.46 10 34.86 10 29.16C 10 22.63 10.55 16.23 11.61 10C 45.45 18.41 83.83 23.16 124.5 23.16C 165.17 23.16 202.83 18.5 236.43 10.24C 237.46 16.39 238 22.71 238 29.16Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.bottomRight.stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 238 29.16C 238 35.01 237.56 40.76 236.71 46.37C 235.37 55.25 232.99 63.8 229.72 71.88C 197.87 79.12 162.29 83.16 124.75 83.16C 87.21 83.16 50.4 78.98 18.13 71.5C 14.88 63.4 12.54 54.84 11.23 45.94C 10.42 40.46 10 34.86 10 29.16C 10 22.63 10.55 16.23 11.61 10C 45.45 18.41 83.83 23.16 124.5 23.16C 165.17 23.16 202.83 18.5 236.43 10.24C 237.46 16.39 238 22.71 238 29.16Z" />
          </g>
          <g
            fill="none"
            stroke={quadrantColors.bottomRight.animatedStroke}
            strokeWidth="2"
            strokeDasharray="4,6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 238 29.16C 238 35.01 237.56 40.76 236.71 46.37C 235.37 55.25 232.99 63.8 229.72 71.88C 197.87 79.12 162.29 83.16 124.75 83.16C 87.21 83.16 50.4 78.98 18.13 71.5C 14.88 63.4 12.54 54.84 11.23 45.94C 10.42 40.46 10 34.86 10 29.16C 10 22.63 10.55 16.23 11.61 10C 45.45 18.41 83.83 23.16 124.5 23.16C 165.17 23.16 202.83 18.5 236.43 10.24C 237.46 16.39 238 22.71 238 29.16Z" />
          </g>
        </g>
      </svg>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[min(90vw,1200px)] mx-auto"
      style={{ backgroundColor: colors.background }}
    >
      {renderTitle()}
      {content.steps.map((_, index) => (
        <div key={index}>{renderQuadrantText(index)}</div>
      ))}
      {renderQuadrantShapes()}
    </div>
  );
}
